import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Progress } from './ui/progress';
import { 
  Building2, 
  Package, 
  Truck, 
  CheckCircle, 
  Users, 
  TrendingUp, 
  Eye, 
  MoreVertical,
  Search,
  Filter,
  Plus,
  MapPin,
  Phone,
  Mail,
  Calendar,
  Activity,
  DollarSign,
  Clock,
  Star,
  Navigation
} from 'lucide-react';
import { useUser } from './UserContext';
import { orderService, getAnalyticsData } from '../api/orderService';
import { getAllPharmacies, getAllDrivers, getDriversByStatus } from '../api/pharmacyService';
import { toast } from 'sonner';
import GlobalLoader from "./ui/GlobalLoader";

export function SuperAdminDashboard() {
  const { pharmacies } = useUser();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [loading, setLoading] = useState(true);
  const [analyticsData, setAnalyticsData] = useState<any>(null);
  const [drivers, setDrivers] = useState<any[]>([]);
  const [systemStats, setSystemStats] = useState({
    totalPharmacies: 0,
    activePharmacies: 0,
    totalOrders: 0,
    todaysOrders: 0,
    activeOrders: 0,
    totalDrivers: 0,
    activeDrivers: 0,
    deliveryRate: 0,
    avgDeliveryTime: 0,
    totalRevenue: 0,
    todayRevenue: 0,
    avgOrderValue: 0
  });

  // Fetch analytics data on component mount
  useEffect(() => {
    const fetchAllData = async () => {
      try {
        setLoading(true);
        
        // Fetch analytics and drivers in parallel
        const [analyticsData, driversData] = await Promise.all([
          getAnalyticsData('7d'),
          getAllDrivers()
        ]);
        
        setAnalyticsData(analyticsData);
        setDrivers(driversData);
        
        // Calculate driver stats
        const activeDrivers = driversData.filter(d => d.status === 'online' || d.status === 'on-delivery').length;
        const onlineDrivers = driversData.filter(d => d.status === 'online').length;
        const deliveryDrivers = driversData.filter(d => d.status === 'on-delivery').length;
        
        // Update system stats with real data
        setSystemStats({
          totalPharmacies: pharmacies.length,
          activePharmacies: pharmacies.filter(p => p.isActive).length,
          totalOrders: analyticsData.basicStats.totalOrders,
          todaysOrders: analyticsData.basicStats.todayOrders,
          activeOrders: analyticsData.basicStats.pendingOrders + analyticsData.basicStats.inTransitOrders,
          totalDrivers: driversData.length,
          activeDrivers: activeDrivers,
          deliveryRate: analyticsData.basicStats.totalOrders > 0 ? 
            ((analyticsData.basicStats.deliveredOrders / analyticsData.basicStats.totalOrders) * 100) : 0,
          avgDeliveryTime: 42, // TODO: Calculate from real data
          totalRevenue: analyticsData.basicStats.totalRevenue,
          todayRevenue: 0, // TODO: Calculate from real data
          avgOrderValue: analyticsData.basicStats.totalOrders > 0 ? 
            (analyticsData.basicStats.totalRevenue / analyticsData.basicStats.totalOrders) : 0
        });
      } catch (error) {
        console.error('Error fetching data:', error);
        toast.error('Failed to load dashboard data');
      } finally {
        setLoading(false);
      }
    };

    fetchAllData();
  }, [pharmacies]);

  const recentActivity = [
    { 
      id: 1, 
      type: 'pharmacy', 
      message: 'New pharmacy "Downtown Medical" completed registration', 
      time: '2 hours ago',
      details: 'License verified, account activated'
    },
    { 
      id: 2, 
      type: 'order', 
      message: 'Peak order volume detected in Manhattan area (67 orders)', 
      time: '4 hours ago',
      details: 'Alert sent to dispatch team'
    },
    { 
      id: 3, 
      type: 'driver', 
      message: '5 new drivers completed onboarding process', 
      time: '6 hours ago',
      details: 'Background checks cleared, training completed'
    },
    { 
      id: 4, 
      type: 'system', 
      message: 'System maintenance completed successfully', 
      time: '1 day ago',
      details: 'Database optimization, 99.9% uptime maintained'
    },
    { 
      id: 5, 
      type: 'delivery', 
      message: 'Record delivery completion rate achieved (98.7%)', 
      time: '2 days ago',
      details: 'Best performance metrics this quarter'
    }
  ];

  const filteredPharmacies = pharmacies.filter(pharmacy => {
    const matchesSearch = pharmacy.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         pharmacy.city.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || 
                         (statusFilter === 'active' && pharmacy.isActive) ||
                         (statusFilter === 'inactive' && !pharmacy.isActive);
    
    return matchesSearch && matchesStatus;
  });

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'pharmacy': return <Building2 className="w-4 h-4" />;
      case 'order': return <Package className="w-4 h-4" />;
      case 'driver': return <Users className="w-4 h-4" />;
      case 'delivery': return <Truck className="w-4 h-4" />;
      case 'system': return <Activity className="w-4 h-4" />;
      default: return <Activity className="w-4 h-4" />;
    }
  };

  const getActivityColor = (type: string) => {
    switch (type) {
      case 'pharmacy': return 'bg-blue-500';
      case 'order': return 'bg-green-500';
      case 'driver': return 'bg-purple-500';
      case 'delivery': return 'bg-orange-500';
      case 'system': return 'bg-gray-500';
      default: return 'bg-gray-500';
    }
  };

  return (
    <div className="space-y-8 max-w-full overflow-hidden">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          {/* <h1 className="text-3xl font-semibold text-gray-900">System Overview</h1> */}
          <p className="text-gray-600 mt-1">Comprehensive platform analytics and management</p>
        </div>
        <div className="flex flex-wrap gap-3">
          <Button className="bg-blue-600 text-white hover:bg-blue-700">
            <Plus className="w-4 h-4 mr-2" />
            Add Pharmacy
          </Button>
          <Button variant="outline">
            <Plus className="w-4 h-4 mr-2" />
            Add Driver
          </Button>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="border-l-4 border-l-blue-500">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-gray-600">Total Pharmacies</p>
                <p className="text-2xl font-bold text-gray-900">{systemStats.totalPharmacies}</p>
                <p className="text-xs text-green-600 mt-1">
                  {systemStats.activePharmacies} active
                </p>
              </div>
              <Building2 className="w-8 h-8 text-blue-600 flex-shrink-0" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-green-500">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-gray-600">Total Orders</p>
                <p className="text-2xl font-bold text-gray-900">{systemStats.totalOrders.toLocaleString()}</p>
                <p className="text-xs text-green-600 mt-1">
                  {systemStats.todaysOrders} today
                </p>
              </div>
              <Package className="w-8 h-8 text-green-600 flex-shrink-0" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-purple-500">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-gray-600">Active Drivers</p>
                <p className="text-2xl font-bold text-gray-900">{systemStats.activeDrivers}</p>
                <p className="text-xs text-gray-500 mt-1">
                  of {systemStats.totalDrivers} total
                </p>
              </div>
              <Truck className="w-8 h-8 text-purple-600 flex-shrink-0" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-orange-500">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-gray-600">Delivery Rate</p>
                <p className="text-2xl font-bold text-gray-900">{systemStats.deliveryRate}%</p>
                <p className="text-xs text-green-600 mt-1">
                  Avg {systemStats.avgDeliveryTime}min
                </p>
              </div>
              <CheckCircle className="w-8 h-8 text-orange-600 flex-shrink-0" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Revenue & Performance Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="bg-gradient-to-r from-green-50 to-green-100 border-green-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-700 font-medium">Total Revenue</p>
                <p className="text-2xl font-bold text-green-800">${systemStats.totalRevenue.toLocaleString()}</p>
                <p className="text-sm text-green-600">Today: ${systemStats.todayRevenue.toLocaleString()}</p>
              </div>
              <DollarSign className="w-8 h-8 text-green-600" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-r from-blue-50 to-blue-100 border-blue-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-700 font-medium">Avg Order Value</p>
                <p className="text-2xl font-bold text-blue-800">${systemStats.avgOrderValue.toFixed(2)}</p>
                <p className="text-sm text-blue-600">+12% vs last month</p>
              </div>
              <TrendingUp className="w-8 h-8 text-blue-600" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-r from-purple-50 to-purple-100 border-purple-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-purple-700 font-medium">Customer Satisfaction</p>
                <p className="text-2xl font-bold text-purple-800">4.8/5.0</p>
                <p className="text-sm text-purple-600">Based on 2,847 reviews</p>
              </div>
              <Star className="w-8 h-8 text-purple-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content Tabs */}
      <Tabs defaultValue="pharmacies" className="space-y-6">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="pharmacies">Pharmacy Network</TabsTrigger>
          <TabsTrigger value="orders">Order Analytics</TabsTrigger>
          <TabsTrigger value="drivers">Driver Management</TabsTrigger>
          <TabsTrigger value="activity">System Activity</TabsTrigger>
        </TabsList>

        {/* Pharmacy Network Tab */}
        <TabsContent value="pharmacies">
          <Card>
            <CardHeader>
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                <CardTitle className="flex items-center space-x-2">
                  <Building2 className="w-5 h-5" />
                  <span>Pharmacy Network ({filteredPharmacies.length})</span>
                </CardTitle>
                <div className="flex flex-wrap items-center gap-2">
                  <div className="relative min-w-0">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <Input
                      placeholder="Search pharmacies..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10 w-64 h-11"
                    />
                  </div>
                  <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger className="w-32">
                      <Filter className="w-4 h-4 mr-2" />
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="inactive">Inactive</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {filteredPharmacies.map((pharmacy) => (
                  <div key={pharmacy.id} className="border rounded-lg p-6 bg-white hover:bg-gray-50 transition-colors">
                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-4">
                      <div className="flex items-center space-x-4">
                        <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          <Building2 className="w-6 h-6 text-blue-600" />
                        </div>
                        <div className="min-w-0">
                          <h3 className="font-semibold text-gray-900">{pharmacy.name}</h3>
                          <p className="text-sm text-gray-500">License: {pharmacy.licenseNumber}</p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Badge className={pharmacy.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                          {pharmacy.isActive ? 'Active' : 'Inactive'}
                        </Badge>
                        <Button variant="ghost" size="sm">
                          <MoreVertical className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                      <div className="flex items-center space-x-2 text-sm">
                        <MapPin className="w-4 h-4 text-gray-400 flex-shrink-0" />
                        <span className="truncate">{pharmacy.city}, {pharmacy.state}</span>
                      </div>
                      <div className="flex items-center space-x-2 text-sm">
                        <Phone className="w-4 h-4 text-gray-400 flex-shrink-0" />
                        <span>{pharmacy.phone}</span>
                      </div>
                      <div className="flex items-center space-x-2 text-sm">
                        <Mail className="w-4 h-4 text-gray-400 flex-shrink-0" />
                        <span className="truncate">{pharmacy.email}</span>
                      </div>
                      <div className="flex items-center space-x-2 text-sm">
                        <Calendar className="w-4 h-4 text-gray-400 flex-shrink-0" />
                        <span>Joined {pharmacy.joinedDate}</span>
                      </div>
                    </div>

                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                      <div className="flex space-x-6">
                        <div>
                          <p className="text-sm text-gray-500">Total Orders</p>
                          <p className="font-semibold">{pharmacy.totalOrders.toLocaleString()}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">Active Orders</p>
                          <p className="font-semibold text-blue-600">{pharmacy.activeOrders}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">Success Rate</p>
                          <p className="font-semibold text-green-600">98.2%</p>
                        </div>
                      </div>
                      <div className="flex space-x-2">
                        <Button variant="outline" size="sm">
                          <Eye className="w-4 h-4 mr-2" />
                          View Details
                        </Button>
                        <Button variant="outline" size="sm">
                          <Activity className="w-4 h-4 mr-2" />
                          Analytics
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Order Analytics Tab */}
        <TabsContent value="orders">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Order Volume Trends</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {loading ? (
                    <div className="text-center py-8">
                      <GlobalLoader isLoading={true} message="Processing please wait..." subMessage="Please wait" />
                      <p className="text-gray-500 mt-2">Loading analytics...</p>
                    </div>
                  ) : analyticsData?.deliveryInsights?.length > 0 ? (
                    analyticsData.deliveryInsights.slice(-7).map((day: any, index: number) => (
                      <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                        <div className="flex-1">
                          <div className="flex justify-between items-center mb-2">
                            <span className="text-sm font-medium">{day.day}</span>
                            <span className="text-sm text-gray-500">{day.deliveries} orders</span>
                          </div>
                          <Progress value={day.successRate} className="h-2" />
                          <div className="flex justify-between text-xs text-gray-500 mt-1">
                            <span>{day.successRate}% success rate</span>
                            <span>{day.avgTime}min avg</span>
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      No analytics data available
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Performance Metrics</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  <div>
                    <div className="flex justify-between text-sm mb-2">
                      <span>Same-Day Delivery Success</span>
                      <span className="font-medium">96.4%</span>
                    </div>
                    <Progress value={96.4} className="h-3" />
                  </div>
                  <div>
                    <div className="flex justify-between text-sm mb-2">
                      <span>Next-Day Delivery Success</span>
                      <span className="font-medium">99.1%</span>
                    </div>
                    <Progress value={99.1} className="h-3" />
                  </div>
                  <div>
                    <div className="flex justify-between text-sm mb-2">
                      <span>Customer Satisfaction</span>
                      <span className="font-medium">96.8%</span>
                    </div>
                    <Progress value={96.8} className="h-3" />
                  </div>
                  <div>
                    <div className="flex justify-between text-sm mb-2">
                      <span>On-Time Performance</span>
                      <span className="font-medium">94.2%</span>
                    </div>
                    <Progress value={94.2} className="h-3" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Driver Management Tab */}
        <TabsContent value="drivers">
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <Card className="bg-green-50 border-green-200">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-green-700 font-medium">Online</p>
                      <p className="text-2xl font-bold text-green-800">
                        {drivers.filter(d => d.status === 'online').length}
                      </p>
                    </div>
                    <Users className="w-8 h-8 text-green-600" />
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-blue-50 border-blue-200">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-blue-700 font-medium">On Delivery</p>
                      <p className="text-2xl font-bold text-blue-800">
                        {drivers.filter(d => d.status === 'on-delivery').length}
                      </p>
                    </div>
                    <Truck className="w-8 h-8 text-blue-600" />
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-yellow-50 border-yellow-200">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-yellow-700 font-medium">On Break</p>
                      <p className="text-2xl font-bold text-yellow-800">
                        {drivers.filter(d => d.status === 'break').length}
                      </p>
                    </div>
                    <Clock className="w-8 h-8 text-yellow-600" />
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-gray-50 border-gray-200">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-gray-700 font-medium">Offline</p>
                      <p className="text-2xl font-bold text-gray-800">
                        {drivers.filter(d => d.status === 'offline').length}
                      </p>
                    </div>
                    <Users className="w-8 h-8 text-gray-600" />
                  </div>
                </CardContent>
              </Card>
            </div>

            <Card>
              <CardHeader>
                <CardTitle>Active Drivers</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {loading ? (
                    <div className="text-center py-8">
                      <GlobalLoader isLoading={true} message="Processing please wait..." subMessage="Please wait" />
                      <p className="text-gray-500 mt-2">Loading drivers...</p>
                    </div>
                  ) : drivers.length > 0 ? (
                    drivers.filter(driver => driver.status !== 'offline').map((driver) => (
                      <div key={driver.id} className="flex items-center justify-between p-4 border rounded-lg">
                        <div className="flex items-center space-x-4">
                          <div className={`w-3 h-3 rounded-full ${
                            driver.status === 'online' ? 'bg-green-500' :
                            driver.status === 'on-delivery' ? 'bg-blue-500' :
                            driver.status === 'break' ? 'bg-yellow-500' : 'bg-gray-500'
                          }`} />
                          <div>
                            <p className="font-medium">{driver.firstName} {driver.lastName}</p>
                            <p className="text-sm text-gray-500">{driver.vehicleType} • {driver.vehiclePlate}</p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-6">
                          <div className="text-center">
                            <p className="text-sm text-gray-500">Rating</p>
                            <p className="font-medium">{driver.rating}/5.0</p>
                          </div>
                          <div className="text-center">
                            <p className="text-sm text-gray-500">Today</p>
                            <p className="font-medium">{driver.completedToday}</p>
                          </div>
                          <div className="text-center">
                            <p className="text-sm text-gray-500">Status</p>
                            <Badge className={
                              driver.status === 'online' ? 'bg-green-100 text-green-800' :
                              driver.status === 'on-delivery' ? 'bg-blue-100 text-blue-800' :
                              driver.status === 'break' ? 'bg-yellow-100 text-yellow-800' : 'bg-gray-100 text-gray-800'
                            }>
                              {driver.status.replace('-', ' ')}
                            </Badge>
                          </div>
                          <Button variant="outline" size="sm">
                            <Navigation className="w-4 h-4 mr-2" />
                            Track
                          </Button>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      <Truck className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                      <p>No drivers found</p>
                      <p className="text-sm">Add drivers to get started</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* System Activity Tab */}
        <TabsContent value="activity">
          <Card>
            <CardHeader>
              <CardTitle>Recent System Activity</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {recentActivity.map((activity) => (
                  <div key={activity.id} className="flex items-start space-x-4 p-4 border rounded-lg hover:bg-gray-50 transition-colors">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${getActivityColor(activity.type)}`}>
                      <div className="text-white">
                        {getActivityIcon(activity.type)}
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900">{activity.message}</p>
                      <p className="text-xs text-gray-500 mt-1">{activity.details}</p>
                      <p className="text-xs text-gray-400 mt-1">{activity.time}</p>
                    </div>
                    <Badge variant="secondary" className="text-xs capitalize">
                      {activity.type}
                    </Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}